import { useGetDashboardStats } from "@workspace/api-client";
import { Users, FileText, Briefcase, Eye, ArrowUpRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export default function Dashboard() {
  const { data: stats, isLoading } = useGetDashboardStats();

  const mockStats = {
    totalLeads: 124,
    newLeads: 12,
    totalPosts: 45,
    publishedPosts: 42,
    totalProjects: 56,
    totalViews: 125000,
    leadsByStatus: [
      { status: "NEW", count: 12 },
      { status: "READ", count: 5 },
      { status: "REPLIED", count: 24 },
      { status: "CONVERTED", count: 78 },
      { status: "LOST", count: 5 }
    ],
    recentLeads: [
      { id: "1", name: "Alice Johnson", company: "TechCorp", subject: "SaaS Platform Rewrite", status: "NEW", createdAt: new Date().toISOString() },
      { id: "2", name: "Bob Smith", company: "HealthInc", subject: "Mobile App MVP", status: "READ", createdAt: new Date(Date.now() - 86400000).toISOString() },
      { id: "3", name: "Charlie Davis", company: "FinTech LLC", subject: "Team Augmentation", status: "NEW", createdAt: new Date(Date.now() - 172800000).toISOString() }
    ]
  };

  const data = stats || mockStats;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your agency metrics.</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Leads</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{data.totalLeads}</div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center text-emerald-500">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                {data.newLeads} new this week
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Projects Delivered</CardTitle>
              <Briefcase className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{data.totalProjects}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Blog Posts</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{data.totalPosts}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {data.publishedPosts} published
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Page Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {data.totalViews >= 1000 
                  ? (data.totalViews / 1000).toFixed(1) + 'k' 
                  : (data.totalViews || 0)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Recent Leads</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : (
              <div className="space-y-4">
                {data?.recentLeads?.map((lead: any) => (
                  <div key={lead.id} className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-muted/20">
                    <div>
                      <p className="font-medium">{lead.name}</p>
                      <p className="text-sm text-muted-foreground">{lead.company} · {lead.subject}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        lead.status === 'NEW' ? 'bg-blue-500/20 text-blue-500' :
                        lead.status === 'READ' ? 'bg-amber-500/20 text-amber-500' :
                        lead.status === 'CONVERTED' ? 'bg-emerald-500/20 text-emerald-500' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {lead.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Lead Funnel</CardTitle>
          </CardHeader>
          <CardContent>
             <div className="space-y-6">
                {data?.leadsByStatus?.map((status: any) => (
                  <div key={status.status}>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-medium">{status.status}</span>
                      <span className="text-muted-foreground">{status.count} ({Math.round((status.count / (data.totalLeads || 1)) * 100)}%)</span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full" 
                        style={{ width: `${(status.count / (data.totalLeads || 1)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
             </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
